<template>
  <div class="w-full pb-4 pt-2">
    <div
      class="group relative flex h-60 w-full items-center justify-center rounded border p-2"
    >
      <!-- Edit button overlay with dimmer (only show for user templates) -->
      <div
        v-if="!props.isDefaultTemplate"
        class="absolute inset-0 z-30 flex cursor-pointer items-center justify-center bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-40"
      >
        <UIButton
          id="edit-whatsapp-template"
          size="small"
          class="z-40 border border-gray-300 bg-white text-black opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100"
          @click="props.openEditModal"
        >
          <Pencil01Icon class="h-6 w-6 text-black" />
          <span class="ml-2 font-medium text-black">{{
            t('labels.edit')
          }}</span>
        </UIButton>
      </div>

      <!-- Disabled overlay for default templates (no edit button, just dimmer) -->
      <div
        v-else
        class="absolute inset-0 z-30 cursor-not-allowed bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-20"
      ></div>

      <!-- WhatsApp Preview Container -->
      <div
        class="whatsapp-preview-container relative h-full w-full overflow-hidden rounded-lg"
      >
        <!-- Background Pattern -->
        <div class="whatsapp-background absolute inset-0"></div>

        <!-- WhatsApp Message Bubble -->
        <div
          class="relative z-10 flex h-full flex-col justify-end overflow-y-auto p-3"
        >
          <div
            class="mb-2 max-h-[calc(100%-30px)] max-w-[85%] self-end overflow-y-auto rounded-lg bg-white p-3 shadow-sm"
          >
            <!-- Header (if exists) -->
            <div
              v-if="headerText"
              class="mb-1 break-words text-[11px] font-bold text-gray-900"
            >
              {{ headerText }}
            </div>

            <!-- Body Text -->
            <div
              v-if="bodyText"
              class="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-800"
            >
              {{ bodyText }}
            </div>

            <!-- Footer (if exists) -->
            <div
              v-if="footerText"
              class="mt-1 break-words text-[9px] text-gray-500"
            >
              {{ footerText }}
            </div>

            <!-- Default message when no template -->
            <div
              v-if="!headerText && !bodyText && !footerText"
              class="text-[11px] text-gray-500"
            >
              {{ t('eventNotification.whatsapp.noPreview') }}
            </div>
          </div>

          <!-- Timestamp -->
          <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
          <div class="mr-1 flex-shrink-0 self-end text-[9px] text-gray-600">
            11:48 am
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Pencil01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UIButton } from '@gohighlevel/ghl-ui'
import { computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface WhatsAppTemplate {
  id: string
  category: string
  components: Array<{
    type: 'HEADER' | 'BODY' | 'FOOTER'
    format?: string
    text: string
    customVariables?: any[]
  }>
  language: string
  name: string
  quality_score?: {
    score: string
    date: number
  }
  rejected_reason?: string
  status: string
  folderId?: string
}

const props = defineProps({
  templateId: {
    type: String,
    required: true,
  },
  template: {
    type: Object as PropType<WhatsAppTemplate>,
    default: null,
  },
  openEditModal: {
    type: Function,
    required: true,
  },
  isDefaultTemplate: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['open'])

// Extract template content
const headerText = computed(() => {
  if (!props.template?.components) return ''
  const headerComponent = props.template.components.find(
    comp => comp.type === 'HEADER'
  )
  return headerComponent?.text || ''
})

const bodyText = computed(() => {
  if (!props.template?.components) return ''
  const bodyComponent = props.template.components.find(
    comp => comp.type === 'BODY'
  )
  return bodyComponent?.text || ''
})

const footerText = computed(() => {
  if (!props.template?.components) return ''
  const footerComponent = props.template.components.find(
    comp => comp.type === 'FOOTER'
  )
  return footerComponent?.text || ''
})

// openEditModal is passed as a prop from parent component
</script>

<style lang="scss" scoped>
.whatsapp-preview-container {
  min-height: 200px;
  background-color: #e5ddd5; /* WhatsApp default background color */
}

.whatsapp-background {
  background-image: url('/src/assets/whatsapp-background-pattern.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.4;
}

/* Custom scrollbar for potential overflow */
.whatsapp-preview-container::-webkit-scrollbar {
  width: 2px;
}

.whatsapp-preview-container::-webkit-scrollbar-track {
  background: transparent;
}

.whatsapp-preview-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 1px;
}
</style>
